import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Buildings, User, BellRinging, BellSlash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { usePush } from "@/hooks/usePush";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-gado-muted">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

async function fileToResizedBase64(file, max = 500) {
  const dataUrl = await new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.readAsDataURL(file);
  });
  const img = await new Promise((res) => {
    const i = new Image();
    i.onload = () => res(i);
    i.src = dataUrl;
  });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
}

export default function Perfil() {
  const { user, updateProfile } = useAuth();
  const push = usePush();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "",
    company_name: "",
    fantasy_name: "",
    person_type: "juridica",
    document: "",
    address: "",
    phone: "",
    logo: null,
  });

  useEffect(() => {
    if (user)
      setF({
        name: user.name || "",
        company_name: user.company_name || "",
        fantasy_name: user.fantasy_name || "",
        person_type: user.person_type || "juridica",
        document: user.document || "",
        address: user.address || "",
        phone: user.phone || "",
        logo: user.logo || null,
      });
  }, [user]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await fileToResizedBase64(file);
      setF((p) => ({ ...p, logo: b64 }));
    } catch {
      toast.error("Não foi possível carregar a imagem");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile(f);
      toast.success("Perfil atualizado");
    } catch {
      toast.error("Erro ao salvar perfil");
    } finally {
      setSaving(false);
    }
  };

  const isJuridica = f.person_type === "juridica";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="perfil-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Perfil da empresa</h1>
        <p className="text-gado-muted">Estes dados aparecem no cabeçalho dos relatórios em PDF.</p>
      </div>

      <div className="rounded-xl border border-gado-line bg-white p-6 flex items-center gap-5">
        <div className="h-20 w-20 rounded-xl border border-gado-line bg-gado-cream grid place-items-center overflow-hidden shrink-0">
          {f.logo ? (
            <img src={f.logo} alt="logo" className="h-full w-full object-cover" />
          ) : (
            <Buildings size={30} className="text-gado-line" />
          )}
        </div>
        <div>
          <p className="font-semibold text-gado-ink mb-1">Logotipo da empresa</p>
          <p className="text-sm text-gado-muted mb-3">PNG ou JPG, quadrado de preferência.</p>
          <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="logo-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="upload-logo-button">
            <UploadSimple size={16} className="mr-2" /> Enviar logo
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-gado-line bg-white p-6" data-testid="notifications-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className={`h-10 w-10 rounded-xl grid place-items-center ${push.subscribed ? "bg-gado-green/10 text-gado-green" : "bg-gado-line/60 text-gado-muted"}`}>
              {push.subscribed ? <BellRinging weight="duotone" size={22} /> : <BellSlash size={22} />}
            </span>
            <div>
              <p className="font-semibold text-gado-ink">Notificações push</p>
              <p className="text-sm text-gado-muted">
                {!push.supported && "Este navegador não suporta notificações."}
                {push.supported && push.subscribed && "Ativas neste dispositivo."}
                {push.supported && !push.subscribed && push.permission === "denied" &&
                  "Bloqueadas nas configurações do navegador."}
                {push.supported && !push.subscribed && push.permission !== "denied" &&
                  "Receba avisos de pesagens e cobranças mesmo com o app fechado."}
              </p>
            </div>
          </div>
          {push.supported && (
            <Switch
              checked={push.subscribed}
              disabled={push.loading || push.permission === "denied"}
              onCheckedChange={async (v) => {
                if (v) {
                  const r = await push.subscribe();
                  if (r.ok) toast.success("Notificações ativadas");
                  else if (r.reason === "denied") toast.error("Permissão negada pelo navegador");
                  else toast.error("Não foi possível ativar");
                } else {
                  await push.unsubscribe();
                  toast.success("Notificações desativadas");
                }
              }}
              data-testid="push-switch"
            />
          )}
        </div>
        {push.subscribed && (
          <Button
            size="sm"
            variant="outline"
            className="mt-4"
            onClick={async () => {
              const r = await push.sendTest();
              if (r?.sent > 0) toast.success(`Notificação enviada (${r.sent})`);
              else toast.error("Nenhuma notificação enviada");
            }}
            data-testid="push-test-button"
          >
            Enviar teste
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-gado-line bg-white p-6 space-y-5">
        <div className="flex items-center justify-between rounded-lg bg-gado-cream border border-gado-line px-4 py-3">
          <div className="flex items-center gap-2">
            <User size={18} className="text-gado-muted" />
            <span className="text-sm font-medium text-gado-ink">
              {isJuridica ? "Pessoa Jurídica (CNPJ)" : "Pessoa Física (CPF)"}
            </span>
          </div>
          <Switch
            checked={isJuridica}
            onCheckedChange={(v) => set("person_type")(v ? "juridica" : "fisica")}
            data-testid="person-type-switch"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Seu nome">
            <Input value={f.name} onChange={set("name")} data-testid="profile-name-input" />
          </Field>
          <Field label={isJuridica ? "CNPJ" : "CPF"}>
            <Input value={f.document} onChange={set("document")} placeholder={isJuridica ? "00.000.000/0000-00" : "000.000.000-00"} data-testid="document-input" />
          </Field>
          <Field label="Razão social / Nome da empresa">
            <Input value={f.company_name} onChange={set("company_name")} data-testid="company-name-input" />
          </Field>
          <Field label="Nome fantasia">
            <Input value={f.fantasy_name} onChange={set("fantasy_name")} data-testid="fantasy-name-input" />
          </Field>
          <Field label="Endereço">
            <Input value={f.address} onChange={set("address")} data-testid="address-input" />
          </Field>
          <Field label="Telefone">
            <Input value={f.phone} onChange={set("phone")} placeholder="(00) 00000-0000" data-testid="phone-input" />
          </Field>
        </div>

        <Button onClick={save} disabled={saving} data-testid="save-profile-button" className="bg-gado-green hover:bg-gado-greenHover text-white">
          {saving ? "Salvando…" : "Salvar alterações"}
        </Button>
      </div>
    </div>
  );
}
